// ApplicationsDashboard.jsx - Separate beautiful component for applications
import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Chart as ChartJS, ArcElement, BarElement, LineElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend } from "chart.js";
import { Bar, Line, Pie, Doughnut, Radar } from "react-chartjs-2";
import { useSelector } from "react-redux";
import SendNotificationModal from "./SendNotification";
import { BsEnvelopePlus } from "react-icons/bs";      // send letter icon
import { HiOutlineMailOpen } from "react-icons/hi";   // view sent letters icon


// Register Chart.js components
ChartJS.register(ArcElement, BarElement, LineElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend);

const ApplicationsDashboard = () => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState("all");

const [selectedApplicant, setSelectedApplicant] = useState(null);
const [showSendModal, setShowSendModal] = useState(false);
const [showSentLettersModal, setShowSentLettersModal] = useState(false);
const [sentLetters, setSentLetters] = useState([]);
const [loadingLetters, setLoadingLetters] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const employerId = currentUser._id;

        // Fetch jobs
        const jobsResponse = await axios.get(`${process.env.REACT_APP_API_URL}job/employers/${employerId}/jobs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const fetchedJobs = Array.isArray(jobsResponse.data.data) ? jobsResponse.data.data : [];
        setJobs(fetchedJobs);

        // Fetch all applications
        let allApps = [];
        for (const job of fetchedJobs) {
          const appResponse = await axios.get(`${process.env.REACT_APP_API_URL}application/getApplicationByJobId/${job._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const apps = Array.isArray(appResponse.data.data) ? appResponse.data.data : [];
          allApps = [...allApps, ...apps.map(app => ({ ...app, jobTitle: job.title }))];
        }
        setApplications(allApps);
        console.log(allApps)
      } catch (error) {
        toast.error("Failed to load applications");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const filteredApps = selectedJobId === "all" ? applications : applications.filter(app => app.jobId === selectedJobId);
console.log(filteredApps)
 
const firstApp = filteredApps[0];
const applicant = firstApp?.applicant?.[0];     
console.log(applicant)
  // Status Distribution (Doughnut)
  const statusCounts = filteredApps.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {});
  const statusData = {
    labels: Object.keys(statusCounts),
    datasets: [{
      data: Object.values(statusCounts),
      backgroundColor: ["#F59E0B", "#4CAF50", "#3B82F6", "#10B981", "#EF4444", "#8B5CF6", "#EC4899"],
    }],
  };

  // Applications per Job (Bar)
  const appsPerJob = jobs.map(job => ({
    title: job.title,
    count: filteredApps.filter(app => app.jobId === job._id).length,
  }));
  const barData = {
    labels: appsPerJob.map(j => j.title),
    datasets: [{
      label: "Applications",
      data: appsPerJob.map(j => j.count),
      backgroundColor: "#7E22CE",
    }],
  };

  // Trend Over Time (Line)
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const currentYear = new Date().getFullYear();
  const trendData = months.map((m, i) => filteredApps.filter(app => new Date(app.createdAt).getMonth() === i).length);
  const lineData = {
    labels: months,
    datasets: [{
      label: "Applications",
      data: trendData,
      borderColor: "#4CAF50",
      tension: 0.4,
    }],
  };

  // Response Time Radar (Average days to status change)
  const radarLabels = ["Applied to Shortlisted", "Shortlisted to Interviewed", "Interviewed to Offer", "Offer to Accepted"];
  const radarData = {
    labels: radarLabels,
    datasets: [{
      label: "Avg Days",
      data: [2, 5, 3, 7], // Placeholder - calculate from real data if timestamps available
      backgroundColor: "rgba(59, 130, 246, 0.2)",
      borderColor: "#3B82F6",
    }],
  };

  const handleSendLetter = (application) => {
  // We need applicant/job/application info for the modal
  const applicant = application.applicant?.[0];
  if (!applicant) return;

  setSelectedApplicant({
    _id: applicant._id,
    firstName: applicant.firstName,
    lastName: applicant.lastName,
    applicationId: application._id,
    jobId: application.job?._id || application.jobId,
    jobTitle: application.job?.jobTitle || "N/A",
  });
  setShowSendModal(true);
};

const handleViewSentLetters = async (applicantId) => {
  if (!applicantId) return;

  setLoadingLetters(true);
  setShowSentLettersModal(true);
  setSentLetters([]);

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}notification/my-sent?recipient=${applicantId}`,
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }
    );
    setSentLetters(res.data.data || []);
  } catch (err) {
    toast.error("Failed to load sent letters");
    console.error(err);
  } finally {
    setLoadingLetters(false);
  }
};


  // Top Applicants Pie (by qualification or experience - placeholder)
  const topApplicantsData = {
    labels: ["Bachelor's", "Master's", "PhD", "Diploma", "High School"],
    datasets: [{
      data: [40, 30, 10, 15, 5],
      backgroundColor: ["#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"],
    }],
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-6 md:p-8 lg:p-10">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Applications Analytics</h2>
        
        {/* Job Filter */}
        <div className="mb-8 flex justify-center">
          <select
            value={selectedJobId}
            onChange={(e) => setSelectedJobId(e.target.value)}
            className="p-3 rounded-xl border border-gray-300 shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Jobs</option>
            {jobs.map(job => (
              <option key={job._id} value={job._id}>{job.title}</option>
            ))}
          </select>
        </div>
         

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {/* Status Distribution */}
          <div className="bg-white p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-2">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Status Distribution</h3>
            <Doughnut data={statusData} options={{ responsive: true, plugins: { legend: { position: "bottom" } } }} />
          </div>

          {/* Applications per Job */}
          <div className="bg-white p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-2 xl:col-span-2">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Applications per Job</h3>
            <Bar data={barData} options={{ responsive: true, scales: { y: { beginAtZero: true } } }} />
          </div>

          {/* Trend Over Time */}
          <div className="bg-white p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-2">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Monthly Trend</h3>
            <Line data={lineData} options={{ responsive: true, scales: { y: { beginAtZero: true } } }} />
          </div>

          {/* Response Time Radar */}
          <div className="bg-white p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-2">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Response Time</h3>
            <Radar data={radarData} options={{ responsive: true }} />
          </div>

          {/* Top Applicants */}
          <div className="bg-white p-8 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:-translate-y-2">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Applicant Qualifications</h3>
            <Pie data={topApplicantsData} options={{ responsive: true, plugins: { legend: { position: "bottom" } } }} />
          </div>
        </div>

        {/* Applications Table */}
        <div className="mt-12 bg-white p-8 rounded-3xl shadow-2xl">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Recent Applications</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-indigo-100 text-indigo-800">
                <tr>
                  <th className="py-4 px-6">Applicant</th>
                  <th className="py-4 px-6">Job</th>
                  <th className="py-4 px-6">Status</th>
                  <th className="py-4 px-6">Applied On</th>
                  <th className="py-4 px-6">Action</th>
                </tr>
              </thead>
          <tbody>
  {filteredApps?.map((application, index) => {
    // Get the applicant from this application (it's inside an array)
    const applicant = application.applicant?.[0];

    // If no applicant → show fallback or skip
    if (!applicant) {
      return (
        <tr key={index}>
          <td colSpan={4} className="py-4 px-6 text-center text-gray-500">
            No applicant data
          </td>
        </tr>
      );
    }

    return (
      <tr key={application._id || index} className="border-b hover:bg-gray-50 transition-colors">
        <td className="py-4 px-6 font-medium">
          {applicant.firstName} {applicant.lastName || "Anonymous"}
        </td>
        <td className="py-4 px-6">{application.job?.jobTitle || "N/A"}</td>
        <td className="py-4 px-6">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              application.status === "Accepted"
                ? "bg-green-100 text-green-800"
                : application.status === "Rejected"
                ? "bg-red-100 text-red-800"
                 : application.status === "Shortlisted"
                ? "bg-blue-100 text-blue-800"
                 : application.status === "Applied"
                ? "bg-blue-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {application.status || "Pending"}
          </span>
        </td>
        <td className="py-4 px-6 text-gray-600">
          {application.createdAt
            ? new Date(application.createdAt).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })
            : "N/A"}
        </td>
          <td className="py-4 px-6">
          <div className="flex items-center gap-4">
            {/* Send Letter Button */}
            <button
              onClick={() => handleSendLetter(application)}
              className="text-indigo-600 hover:text-indigo-800 transition-colors"
              title="Send Notification Letter"
            >
              <BsEnvelopePlus size={22} />
            </button>

            {/* View Sent Letters */}
            <button
              onClick={() => handleViewSentLetters(applicant._id)}
              className="text-teal-600 hover:text-teal-800 transition-colors"
              title="View Previously Sent Letters"
            >
              <HiOutlineMailOpen size={22} />
            </button>
          </div>
        </td>
      </tr>
    );
  })}
</tbody>
            </table>
          </div>
        </div>
      </div>
    {/* Send Letter Modal */}
{showSendModal && selectedApplicant && (
  <SendNotificationModal
    isOpen={showSendModal}
    onClose={() => setShowSendModal(false)}
    application={{
      _id: selectedApplicant.applicationId,
      jobSeeker: {
        _id: selectedApplicant._id,
        firstName: selectedApplicant.firstName,
        lastName: selectedApplicant.lastName,
      },
      job: {
        _id: selectedApplicant.jobId,
        title: selectedApplicant.jobTitle,
      },
    }}
  />
)}

{/* View Sent Letters Modal */}
{showSentLettersModal && (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
      <div className="p-6 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Letters Sent to {selectedApplicant?.firstName} {selectedApplicant?.lastName}
          </h2>
          <button
            onClick={() => setShowSentLettersModal(false)}
            className="text-gray-500 hover:text-gray-800 text-2xl"
          >
            ×
          </button>
        </div>

        {loadingLetters ? (
          <div className="text-center py-12">Loading letters...</div>
        ) : sentLetters.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No letters have been sent to this applicant yet.
          </div>
        ) : (
          <div className="space-y-6">
            {sentLetters.map((letter) => (
              <div key={letter._id} className="border rounded-lg p-5 bg-gray-50">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-semibold text-lg">{letter.title}</h4>
                  <span className="text-sm text-gray-500">
                    {new Date(letter.createdAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="text-sm whitespace-pre-line leading-relaxed">
                  {letter.message}
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Type: <span className="font-medium">{letter.type.replace("_", " ")}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default ApplicationsDashboard;




































